import { useMemo } from "react";
import { interpolate, Sequence, useVideoConfig } from "remotion";
import { Audio } from "@remotion/media";
import { useAudioData } from "@remotion/media-utils";

import { CompositionDto, VoiceOverDto } from "@repo/api/editor";
import { BackgroundMusicDto } from "@repo/api/background-music";
import { getMediaSrc } from "../utils/media.ts";
import { easeOutCubic } from "../utils/easing.ts";
import {
  CompositionTimings,
  getVoiceOverStartFrame,
  msToFrames,
} from "../utils/timing.ts";
import {
  detectSoundSections,
  getDuckedVolume,
  getDuckingAmount,
  SoundSection,
} from "../utils/music-ducking.ts";
import {
  getBackgroundMusicVolume,
  isAutoDuckingEnabled,
} from "@repo/api/utils/composition";

// Loudness detection doesn't need full fidelity; this keeps decoding cheap.
const DUCKING_AUDIO_DATA_OPTIONS = { sampleRate: 8000 };

type Props = {
  composition: CompositionDto;
  backgroundMusics: BackgroundMusicDto[];
  voiceOvers: VoiceOverDto[];
  timings: CompositionTimings;
  durationInFrames: number;
};

export default function BackgroundMusic({
  composition,
  backgroundMusics,
  voiceOvers,
  timings,
  durationInFrames,
}: Props) {
  const { fps } = useVideoConfig();

  const compositionBackgroundMusic = composition.backgroundMusic;

  if (!compositionBackgroundMusic) {
    return null;
  }

  const backgroundMusic = backgroundMusics.find(
    (backgroundMusic) =>
      backgroundMusic.externalId === compositionBackgroundMusic.externalId,
  );

  if (!backgroundMusic) {
    return null;
  }

  const audioProps: BackgroundMusicAudioProps = {
    composition,
    compositionBackgroundMusic,
    backgroundMusic,
    durationInFrames,
    duckingSections: [],
    duckingStartFrame: 0,
  };

  const voiceOver = voiceOvers.find(
    (voiceOver) => voiceOver.externalId === composition.voiceOver?.externalId,
  );

  if (
    !voiceOver ||
    (composition.voiceOver?.volume ?? 1) <= 0 ||
    !isAutoDuckingEnabled(composition)
  ) {
    return <BackgroundMusicAudio {...audioProps} />;
  }

  return (
    <DuckedBackgroundMusic
      {...audioProps}
      duckingSrc={getMediaSrc(voiceOver.src)}
      duckingStartFrame={getVoiceOverStartFrame(
        timings,
        composition.voiceOver,
        fps,
      )}
    />
  );
}

function DuckedBackgroundMusic({
  duckingSrc,
  ...props
}: BackgroundMusicAudioProps & { duckingSrc: string }) {
  const audioData = useAudioData(duckingSrc, DUCKING_AUDIO_DATA_OPTIONS);

  const duckingSections = useMemo(
    () =>
      audioData
        ? detectSoundSections(audioData.channelWaveforms, audioData.sampleRate)
        : [],
    [audioData],
  );

  return <BackgroundMusicAudio {...props} duckingSections={duckingSections} />;
}

type BackgroundMusicAudioProps = {
  composition: CompositionDto;
  compositionBackgroundMusic: NonNullable<CompositionDto["backgroundMusic"]>;
  backgroundMusic: BackgroundMusicDto;
  durationInFrames: number;
  duckingSections: SoundSection[];
  duckingStartFrame: number;
};

function BackgroundMusicAudio({
  composition,
  compositionBackgroundMusic,
  backgroundMusic,
  durationInFrames,
  duckingSections,
  duckingStartFrame,
}: BackgroundMusicAudioProps) {
  const { fps } = useVideoConfig();

  const backgroundMusicVolume = getBackgroundMusicVolume(composition);

  const fadeInFrames = msToFrames(
    compositionBackgroundMusic.fadeInMs ?? 0,
    fps,
  );
  const fadeOutFrames = msToFrames(
    compositionBackgroundMusic.fadeOutMs ?? 1000,
    fps,
  );

  const songStartFrame =
    compositionBackgroundMusic.startMs !== undefined
      ? msToFrames(compositionBackgroundMusic.startMs, fps)
      : 0;

  const startSilenceFrames = msToFrames(
    compositionBackgroundMusic.startSilenceMs ?? 0,
    fps,
  );
  const endSilenceFrames = msToFrames(
    compositionBackgroundMusic.endSilenceMs ?? 0,
    fps,
  );

  const startFromFrame = songStartFrame;

  const audibleFrames = Math.max(
    0,
    durationInFrames - startSilenceFrames - endSilenceFrames,
  );

  if (audibleFrames <= 0) {
    return null;
  }

  const fadeInEndFrame = Math.min(fadeInFrames, audibleFrames);
  const fadeOutStartFrame = Math.max(0, audibleFrames - fadeOutFrames);
  const fadeOutEndFrame = Math.max(0, audibleFrames - 1);

  // `f` in the volume callback is relative to this Sequence, not the composition.
  const duckingOffsetMs =
    ((duckingStartFrame - startSilenceFrames) / fps) * 1000;

  return (
    <Sequence
      from={startSilenceFrames}
      durationInFrames={audibleFrames}
      style={{ pointerEvents: "none", zIndex: 1 }}
      premountFor={msToFrames(2000, fps)}
    >
      <Audio
        src={getMediaSrc(backgroundMusic.src)}
        trimBefore={startFromFrame}
        pauseWhenBuffering={true}
        volume={(f: number) => {
          let fadeInMultiplier = 1;
          if (fadeInFrames > 0 && fadeInEndFrame > 0 && f < fadeInEndFrame) {
            fadeInMultiplier = interpolate(f, [0, fadeInEndFrame], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
              easing: easeOutCubic,
            });
          }

          let fadeOutMultiplier = 1;
          if (
            fadeOutFrames > 0 &&
            fadeOutEndFrame > fadeOutStartFrame &&
            f >= fadeOutStartFrame
          ) {
            fadeOutMultiplier = interpolate(
              f,
              [fadeOutStartFrame, fadeOutEndFrame],
              [1, 0],
              {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
                easing: easeOutCubic,
              },
            );
          }

          const duckingAmount = getDuckingAmount(
            (f / fps) * 1000 - duckingOffsetMs,
            duckingSections,
          );

          return (
            getDuckedVolume(backgroundMusicVolume, duckingAmount) *
            fadeInMultiplier *
            fadeOutMultiplier
          );
        }}
      />
    </Sequence>
  );
}
